/**
 * ╔════════════════════════════════════════════════════════════════════════╗
 * ║ gestao.ts — o que o PAINEL pergunta ao banco                            ║
 * ╚════════════════════════════════════════════════════════════════════════╝
 *
 * Sessão, fila de formulários, fila de respostas, detalhe, auditoria e
 * pessoas. Tudo que a organizadora e o operador enxergam entra por aqui.
 *
 * Três regras herdadas de `api.ts`, e nenhuma delas é opinião:
 *
 *  1. **Não importa `@supabase/supabase-js`.** O cliente chega pronto de
 *     `api.ts`, que é o único a conhecer o pacote. `testes/disciplina.test.ts`
 *     quebra o build se um segundo arquivo o importar — inclusive este.
 *  2. **A tela não soma.** KPI, taxa e agregação saem de `painel_formulario` e
 *     das funções de `0006_relatorio.sql` prontos. Nada aqui divide, conta ou
 *     acumula: duas cópias da mesma conta discordam em produção e ninguém sabe
 *     qual está certa.
 *  3. **Taxa sem denominador é `null`, nunca `0`.** O SQL se dá ao trabalho de
 *     preservar a diferença entre "ninguém confirmou" e "ninguém foi
 *     convidado"; um `?? 0` aqui apagaria justamente isso. Quem formata é
 *     `util/formato.ts`, que devolve `—`.
 *
 * O que ESTE arquivo faz além de repassar: traduz erro do Postgres para frase
 * que o operador entende (§ `mensagemDoErro`) e normaliza número que o
 * PostgREST às vezes entrega como texto. Nenhuma das duas é conta.
 */

import type {
  Auditoria,
  Condicao,
  EstadoEnvio,
  EstadoResposta,
  Formulario,
  Papel,
  Perfil,
  Resposta,
  SubtipoPergunta,
  Valor,
} from '../tipos';
import { clientePainel, ErroDaApi, exigirEfeito } from './api';
import { data } from '../util/formato';

/* ══ Resposta do PostgREST ═══════════════════════════════════════════════ */

type RespostaDoBanco<T> = {
  data: T | null;
  error: { message: string; code?: string; details?: string | null } | null;
};

function ouErro<T>(r: RespostaDoBanco<T>, oQueFalhou: string): T {
  if (r.error) {
    throw new ErroDaApi(r.error.message, r.error.code ?? null, r.error.details ?? null);
  }
  if (r.data === null || r.data === undefined) {
    throw new ErroDaApi(`${oQueFalhou}: o banco não devolveu nada`);
  }
  return r.data;
}

/**
 * `numeric` atravessa o PostgREST como número na maioria dos casos e como
 * texto em alguns — depende da versão e do caminho (coluna vs. chave de
 * `jsonb_build_object`). Normalizar na entrada é o que impede `"0.75"` de
 * chegar em `porcento()` e voltar `—` como se não houvesse denominador.
 *
 * Isto NÃO é conta: não divide, não soma, só lê o mesmo valor que o banco
 * mandou. Ausência continua ausência.
 */
function fracao(valor: unknown): number | null {
  if (valor === null || valor === undefined) return null;
  const n = typeof valor === 'string' ? Number(valor) : valor;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
}

function inteiro(valor: unknown): number {
  const n = fracao(valor);
  return n === null ? 0 : n;
}

/* ══ Erro legível ════════════════════════════════════════════════════════
 *
 * A RLS recusa de novo o que a interface já não ofereceu — e é isso que
 * mantém o controle de acesso no banco em vez de na tela. Mas recusa da RLS
 * chega como um código de cinco caracteres, e um código de cinco caracteres na
 * tela é indistinguível de tela branca para quem está tentando corrigir uma
 * confirmação errada entre duas reuniões.
 *
 * Erro diz o que fazer, não pede desculpa. Por isso os códigos que a política
 * do banco produz viram frase; qualquer outro atravessa inteiro, porque as
 * mensagens do motor (`raise exception 'não dá para publicar: %'`) já são
 * escritas para humano e reescrevê-las aqui só criaria um texto desatualizado.
 */

/** Recusa da política de linha do Postgres. */
const INSUFICIENTE = '42501';
/** O `single()`/`maybeSingle()` que não achou linha — quase sempre RLS. */
const NENHUMA_LINHA = 'PGRST116';
/** JWT expirado ou ausente. */
const SEM_SESSAO = 'PGRST301';

export function mensagemDoErro(erro: unknown, oQueTentava: string): string {
  if (!(erro instanceof ErroDaApi)) {
    return erro instanceof Error && erro.message !== ''
      ? erro.message
      : `Não foi possível ${oQueTentava}.`;
  }

  switch (erro.codigo) {
    case INSUFICIENTE:
      return (
        `Seu papel nesta organização não permite ${oQueTentava}. ` +
        'Peça a quem é proprietário para trocar seu papel em Pessoas.'
      );
    case NENHUMA_LINHA:
      return (
        `Nada mudou ao ${oQueTentava}: ou a linha não existe mais, ou seu papel ` +
        'não alcança ela. Recarregue a fila para ver o estado atual.'
      );
    case SEM_SESSAO:
      return 'Sua sessão expirou. Entre de novo para continuar.';
    default:
      return erro.message;
  }
}

/** Verdadeiro quando a recusa veio da política do banco, e não de rede. */
export function ehRecusaDePermissao(erro: unknown): boolean {
  return erro instanceof ErroDaApi && (erro.codigo === INSUFICIENTE || erro.codigo === SEM_SESSAO);
}

/* ══ Papéis: o que a tela OFERECE ════════════════════════════════════════
 *
 * Esconder botão não é controle de acesso. Estas funções existem para a
 * interface fazer sentido — oferecer "Publicar" a quem é `leitor` é prometer
 * algo que o banco vai recusar —, e não para impedir nada. Quem impede é
 * `0003_rls.sql`, e a recusa dele vira frase em `mensagemDoErro`.
 *
 * A tabela é a de `docs/ESPECIFICACAO.md` §3, copiada aqui de propósito: se
 * ela mudar lá, este arquivo tem que mudar junto, e o `tsc` não avisa.
 */

export function podeEditarFormulario(papel: Papel | null): boolean {
  return papel === 'proprietario' || papel === 'administrador';
}

export function podeApagarFormulario(papel: Papel | null): boolean {
  return papel === 'proprietario';
}

export function podeEditarResposta(papel: Papel | null): boolean {
  return papel === 'proprietario' || papel === 'administrador' || papel === 'operador';
}

/** Exportar é de `operador` para cima — `leitor` só lê. */
export function podeExportar(papel: Papel | null): boolean {
  return podeEditarResposta(papel);
}

export function podeVerAuditoria(papel: Papel | null): boolean {
  return papel === 'proprietario' || papel === 'administrador';
}

export function podeGerirPessoas(papel: Papel | null): boolean {
  return papel === 'proprietario';
}

/* ══ Painel do formulário ════════════════════════════════════════════════ */

export type ContagemPorFim = {
  readonly no_id: string;
  readonly rotulo: string;
  readonly total: number;
};

export type PainelDoFormulario = {
  readonly total: number;
  readonly concluidas: number;
  readonly em_andamento: number;
  readonly abandonadas: number;
  /** Fração de 0 a 1, ou `null` quando não há resposta nenhuma. */
  readonly taxa_conclusao: number | null;
  readonly convites_enviados: number;
  readonly convites_respondidos: number;
  /** `null` quando nenhum convite foi enviado — não é 0%. */
  readonly taxa_resposta: number | null;
  readonly por_etiqueta: Readonly<Record<string, number>>;
  readonly por_fim: readonly ContagemPorFim[];
  /** §9: a fila que espera transporte. Cresce em silêncio se a tela calar. */
  readonly envios_pendentes: number;
  readonly envios_falhos: number;
  readonly envios_pendentes_por_canal: Readonly<Record<string, number>>;
};

/** O painel de um formulário que ainda não tem nada — para o esqueleto e o vazio. */
export const PAINEL_VAZIO: PainelDoFormulario = {
  total: 0,
  concluidas: 0,
  em_andamento: 0,
  abandonadas: 0,
  taxa_conclusao: null,
  convites_enviados: 0,
  convites_respondidos: 0,
  taxa_resposta: null,
  por_etiqueta: {},
  por_fim: [],
  envios_pendentes: 0,
  envios_falhos: 0,
  envios_pendentes_por_canal: {},
};

function lerPainel(cru: Record<string, unknown>): PainelDoFormulario {
  return {
    total: inteiro(cru['total']),
    concluidas: inteiro(cru['concluidas']),
    em_andamento: inteiro(cru['em_andamento']),
    abandonadas: inteiro(cru['abandonadas']),
    taxa_conclusao: fracao(cru['taxa_conclusao']),
    convites_enviados: inteiro(cru['convites_enviados']),
    convites_respondidos: inteiro(cru['convites_respondidos']),
    taxa_resposta: fracao(cru['taxa_resposta']),
    por_etiqueta: (cru['por_etiqueta'] as Record<string, number> | null) ?? {},
    por_fim: (cru['por_fim'] as ContagemPorFim[] | null) ?? [],
    envios_pendentes: inteiro(cru['envios_pendentes']),
    envios_falhos: inteiro(cru['envios_falhos']),
    envios_pendentes_por_canal:
      (cru['envios_pendentes_por_canal'] as Record<string, number> | null) ?? {},
  };
}

export async function painelDoFormulario(formularioId: string): Promise<PainelDoFormulario> {
  const r = await clientePainel().rpc('painel_formulario', { p_formulario_id: formularioId });
  const cru = ouErro<Record<string, unknown>>(
    r as RespostaDoBanco<Record<string, unknown>>,
    'ler o painel do formulário',
  );
  return lerPainel(cru);
}

/* ══ Fila de formulários ═════════════════════════════════════════════════ */

export type FormularioNaFila = {
  readonly formulario: Formulario;
  readonly painel: PainelDoFormulario;
};

/**
 * A fila com os números de cada linha.
 *
 * **Uma chamada a `painel_formulario` por formulário, em paralelo.** Não há
 * função plural em `0006` — e a alternativa seria contar as respostas aqui, o
 * que é exatamente o que a regra 2 proíbe. Um `count` no cliente e o
 * `painel_formulario` da tela seguinte discordariam no dia em que alguém
 * mudasse o critério de "concluída" no SQL, e a fila mostraria um número que
 * o detalhe desmente.
 *
 * O custo é aceitável porque a fila de quem organiza um evento tem dezenas de
 * linhas, não milhares. Se um dia tiver, o lugar de resolver é o banco.
 */
export async function filaDeFormularios(): Promise<FormularioNaFila[]> {
  const r = await clientePainel()
    .from('formulario')
    .select('*')
    .order('atualizado_em', { ascending: false });
  const formularios = ouErro<Formulario[]>(
    r as RespostaDoBanco<Formulario[]>,
    'listar formulários',
  );

  return Promise.all(
    formularios.map(async (formulario) => ({
      formulario,
      painel: await painelDoFormulario(formulario.id),
    })),
  );
}

/**
 * Apaga um formulário — leva as respostas junto por cascade, e por isso a RLS
 * de `0003` restringe a `proprietario` (§3). A função em si não pede
 * confirmação nenhuma: quem confirma é a tela (`Formularios.tsx`), porque
 * confirmar é decisão de interface, não de persistência.
 *
 * `api.ts` já tem uma versão (usada por `modelos/aplicar.ts` para desfazer um
 * modelo que falhou), e mesmo assim esta é implementada aqui de novo — pelo
 * mesmo motivo de `painelDoFormulario` acima: a fila fala com o banco só por
 * este arquivo, e delegar faria a chamada atravessar o cliente privado de
 * `api.ts` em vez do `clientePainel()` que este arquivo mocka em teste.
 *
 * O que importa não duplicar é `exigirEfeito`: zero linhas apagadas é a RLS
 * recusando, não sucesso, e sem passar por ela a tela mostraria "apagado"
 * sobre um banco intacto — defeito real em outras sete escritas deste
 * projeto antes de `exigirEfeito` existir.
 */
export async function apagarFormulario(id: string): Promise<void> {
  const r = await clientePainel().from('formulario').delete().eq('id', id).select('id');
  exigirEfeito(r, 'apagar o formulário');
}

/* ══ Fila de respostas ═══════════════════════════════════════════════════ */

export type ItemDaResposta = {
  readonly no_id: string;
  readonly rotulo: string;
  readonly subtipo: SubtipoPergunta | null;
  readonly valor: Valor;
  /** O que `valor_texto()` produziu — já legível, inclusive para lista. */
  readonly texto: string | null;
};

export type LinhaDeResposta = {
  readonly resposta_id: string;
  readonly versao_numero: number;
  readonly estado: EstadoResposta;
  readonly etiquetas: readonly string[];
  /** §10: dado corrigido em silêncio é dado que ninguém pode mais auditar. */
  readonly editada: boolean;
  readonly convite_nome: string | null;
  readonly convite_email: string | null;
  readonly iniciada_em: string;
  readonly concluida_em: string | null;
  /** O rótulo do nó `fim` alcançado — o cenário em que a resposta caiu. */
  readonly fim_rotulo: string | null;
  readonly no_atual_rotulo: string | null;
  readonly itens: readonly ItemDaResposta[];
  /** O total DEPOIS do filtro, para a paginação. Vem do banco, não daqui. */
  readonly total_filtrado: number;
};

/**
 * O filtro visível. É o MESMO objeto que a exportação recebe — §10 manda a
 * exportação respeitar o filtro da tela, e passar os dois pela mesma estrutura
 * é o que impede a planilha de trazer linha que a lista não mostrava.
 */
export type FiltroDeRespostas = {
  readonly estado: EstadoResposta | null;
  readonly etiqueta: string | null;
  /**
   * O período, como DATA LOCAL no formato `aaaa-mm-dd` — o que um
   * `<input type="date">` devolve.
   *
   * A conversão para instante acontece na borda da rede (`limitesDoPeriodo`), e
   * não aqui, porque só o navegador sabe o fuso de quem está lendo. "12/09" para
   * quem está em São Paulo não é o mesmo intervalo que "12/09" em UTC, e guardar
   * um `Date` no filtro esconderia essa decisão dentro de um objeto que a tela
   * também lê para desenhar os campos.
   */
  readonly de: string | null;
  readonly ate: string | null;
  /** Nome, e-mail ou texto de resposta. Casa por trecho, ignorando caixa. */
  readonly busca: string | null;
};

export const SEM_FILTRO: FiltroDeRespostas = {
  estado: null,
  etiqueta: null,
  de: null,
  ate: null,
  busca: null,
};

export function filtroEstaVazio(filtro: FiltroDeRespostas): boolean {
  return (
    filtro.estado === null &&
    filtro.etiqueta === null &&
    filtro.de === null &&
    filtro.ate === null &&
    (filtro.busca === null || filtro.busca.trim() === '')
  );
}

/**
 * A data local do filtro vira o instante que o banco espera.
 *
 * Duas decisões moram aqui, e as duas são de fuso:
 *
 *  - `new Date('2026-09-12')` é interpretado como **UTC** pelo JavaScript, e
 *    `new Date(2026, 8, 12)` como local. A segunda é a que queremos: quem pediu
 *    "12/09" pediu o dia dele, não o dia de Greenwich. Um `Date` construído
 *    errado adianta ou atrasa o recorte em algumas horas, e o efeito é uma
 *    resposta da noite aparecendo no dia seguinte.
 *  - O limite de cima é o **início do dia seguinte**, porque o SQL usa `<`
 *    exclusivo (ver `0011`). Mandar 23:59:59 perderia o que aconteceu no último
 *    segundo do dia — pouco, e invisível, que é a pior combinação.
 */
export function limitesDoPeriodo(filtro: FiltroDeRespostas): {
  readonly de: string | null;
  readonly ate: string | null;
} {
  return { de: inicioDoDia(filtro.de), ate: inicioDoDiaSeguinte(filtro.ate) };
}

function partesDaData(iso: string | null): [number, number, number] | null {
  if (!iso) return null;
  const casou = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!casou) return null;
  const [, ano, mes, dia] = casou;
  return [Number(ano), Number(mes) - 1, Number(dia)];
}

function inicioDoDia(iso: string | null): string | null {
  const partes = partesDaData(iso);
  if (!partes) return null;
  return new Date(partes[0], partes[1], partes[2], 0, 0, 0, 0).toISOString();
}

function inicioDoDiaSeguinte(iso: string | null): string | null {
  const partes = partesDaData(iso);
  if (!partes) return null;
  return new Date(partes[0], partes[1], partes[2] + 1, 0, 0, 0, 0).toISOString();
}

/** Descreve o filtro em prosa, para a tela dizer o que a planilha vai conter. */
export function descreverFiltro(filtro: FiltroDeRespostas): string {
  const partes: string[] = [];
  if (filtro.estado) partes.push(ROTULO_DE_ESTADO[filtro.estado].toLowerCase());
  if (filtro.etiqueta) partes.push(`com a etiqueta “${filtro.etiqueta}”`);
  if (filtro.busca && filtro.busca.trim() !== '')
    partes.push(`que mencionam “${filtro.busca.trim()}”`);

  // O período fecha a frase porque é o que se lê por último em voz alta: "as
  // respostas concluídas de 01/09 a 12/09".
  const de = filtro.de === null ? null : data(inicioDoDia(filtro.de));
  const ate = filtro.ate === null ? null : data(inicioDoDia(filtro.ate));
  if (de && ate) partes.push(`de ${de} a ${ate}`);
  else if (de) partes.push(`a partir de ${de}`);
  else if (ate) partes.push(`até ${ate}`);

  return partes.length === 0 ? 'todas as respostas' : `as respostas ${partes.join(' ')}`;
}

export const ROTULO_DE_ESTADO: Readonly<Record<EstadoResposta, string>> = {
  em_andamento: 'Em andamento',
  concluida: 'Concluída',
  abandonada: 'Abandonada',
};

export const ROTULO_DE_ENVIO: Readonly<Record<EstadoEnvio, string>> = {
  pendente: 'Aguardando transporte',
  enviado: 'Enviado',
  falhou: 'Falhou',
  cancelado: 'Cancelado',
};

function lerLinha(cru: Record<string, unknown>): LinhaDeResposta {
  return {
    resposta_id: String(cru['resposta_id']),
    versao_numero: inteiro(cru['versao_numero']),
    estado: cru['estado'] as EstadoResposta,
    etiquetas: (cru['etiquetas'] as string[] | null) ?? [],
    editada: cru['editada'] === true,
    convite_nome: (cru['convite_nome'] as string | null) ?? null,
    convite_email: (cru['convite_email'] as string | null) ?? null,
    iniciada_em: String(cru['iniciada_em']),
    concluida_em: (cru['concluida_em'] as string | null) ?? null,
    fim_rotulo: (cru['fim_rotulo'] as string | null) ?? null,
    no_atual_rotulo: (cru['no_atual_rotulo'] as string | null) ?? null,
    itens: (cru['itens'] as ItemDaResposta[] | null) ?? [],
    total_filtrado: inteiro(cru['total_filtrado']),
  };
}

/**
 * Quantas respostas cabem numa página da fila.
 *
 * Existe como constante exportada porque três lugares precisam do MESMO
 * número: a consulta que pede as linhas, o cálculo do deslocamento e a frase
 * que diz "mostrando 101–200 de 350". Quando esse número morava só no default
 * do parâmetro, a tela desenhava 100 linhas de 350 e escrevia "350 respostas
 * recebidas" logo acima — e quem não achasse alguém na tabela concluía que a
 * pessoa não tinha respondido.
 */
export const TAMANHO_DA_PAGINA = 100;

export async function respostasDoFormulario(
  formularioId: string,
  filtro: FiltroDeRespostas = SEM_FILTRO,
  limite = TAMANHO_DA_PAGINA,
  deslocamento = 0,
): Promise<LinhaDeResposta[]> {
  const periodo = limitesDoPeriodo(filtro);
  const r = await clientePainel().rpc('respostas_do_formulario', {
    p_formulario_id: formularioId,
    p_estado: filtro.estado,
    p_etiqueta: filtro.etiqueta,
    p_limite: limite,
    p_deslocamento: deslocamento,
    p_de: periodo.de,
    p_ate: periodo.ate,
    p_busca: filtro.busca,
  });
  const cru = ouErro<Record<string, unknown>[]>(
    r as RespostaDoBanco<Record<string, unknown>[]>,
    'listar respostas',
  );
  return cru.map(lerLinha);
}

/* ══ Detalhe da resposta ═════════════════════════════════════════════════ */

/** Um passo do caminho percorrido. É registro: lê-se, não se edita (0003). */
export type PassoDoPercurso = {
  readonly id: number;
  readonly no_id: string;
  readonly no_rotulo: string;
  readonly no_tipo: string;
  /** A aresta que trouxe até aqui. `null` no primeiro passo. */
  readonly aresta_id: string | null;
  /** A condição da aresta. `null` é o SENÃO — o caminho de quando nada casou. */
  readonly condicao: Condicao | null;
  readonly em: string;
};

type PercursoCru = {
  id: number;
  no_id: string;
  aresta_id: string | null;
  em: string;
  no: { rotulo: string; tipo: string } | null;
  aresta: { condicao: Condicao | null } | null;
};

/**
 * O percurso, com o rótulo do nó e a condição da aresta já juntos.
 *
 * O join vem do PostgREST porque a alternativa — buscar os nós e casar aqui —
 * exigiria conhecer a versão e refazer o mapeamento a cada render. E o
 * percurso é a resposta para "por que esta pessoa caiu neste cenário": sem a
 * condição ao lado do passo, ele vira uma lista de nomes sem causa.
 */
export async function percursoDaResposta(respostaId: string): Promise<PassoDoPercurso[]> {
  const r = await clientePainel()
    .from('resposta_percurso')
    .select('id, no_id, aresta_id, em, no:no_id(rotulo, tipo), aresta:aresta_id(condicao)')
    .eq('resposta_id', respostaId)
    .order('id');
  const cru = ouErro<PercursoCru[]>(r as RespostaDoBanco<PercursoCru[]>, 'ler o percurso');

  return cru.map((p) => ({
    id: p.id,
    no_id: p.no_id,
    no_rotulo: p.no?.rotulo ?? '',
    no_tipo: p.no?.tipo ?? '',
    aresta_id: p.aresta_id,
    condicao: p.aresta?.condicao ?? null,
    em: p.em,
  }));
}

export type DetalheDaResposta = {
  readonly resposta: Resposta;
  readonly versao_numero: number;
  readonly convite_nome: string | null;
  readonly convite_email: string | null;
  /** O rótulo do nó onde a resposta parou — o cenário, quando é um `fim`. */
  readonly no_atual_rotulo: string | null;
  readonly no_atual_e_fim: boolean;
  /** Os itens na ordem em que as perguntas são FEITAS, medida no grafo. */
  readonly itens: readonly ItemDaResposta[];
  /** `no_id` → rótulo, para o percurso poder citar a pergunta pelo nome. */
  readonly rotuloPorNo: Readonly<Record<string, string>>;
};

type NoDoDetalhe = { id: string; rotulo: string; subtipo: SubtipoPergunta | null; tipo: string };

/**
 * O detalhe de UMA resposta.
 *
 * Não passa por `respostas_do_formulario`: aquela função é a fila, e pedir a
 * fila inteira para achar uma linha faria a tela de detalhe custar o
 * formulário todo — num evento com mil confirmações, para mostrar uma.
 *
 * A ordem dos itens vem de `ordem_dos_nos`, que é a ordem em que as perguntas
 * são feitas medida no grafo. `resposta_item.respondido_em` não serve: uma
 * resposta gravada em lote tem o mesmo instante em vários itens, e o desempate
 * cairia no uuid — a tela sairia com as perguntas embaralhadas, numa ordem
 * diferente da que o convidado leu.
 */
export async function detalheDaResposta(respostaId: string): Promise<DetalheDaResposta> {
  const cliente = clientePainel();

  const cru = await cliente
    .from('resposta')
    .select(
      '*, versao:versao_id(numero), convite:convite_id(nome, email), atual:no_atual_id(rotulo, tipo)',
    )
    .eq('id', respostaId)
    .single();
  const linha = ouErro<
    Resposta & {
      versao: { numero: number } | null;
      convite: { nome: string; email: string | null } | null;
      atual: { rotulo: string; tipo: string } | null;
    }
  >(cru as RespostaDoBanco<never>, 'ler a resposta');

  const [nosCrus, itensCrus, ordemCrua] = await Promise.all([
    cliente.from('no').select('id, rotulo, subtipo, tipo').eq('versao_id', linha.versao_id),
    cliente.from('resposta_item').select('no_id, valor').eq('resposta_id', respostaId),
    cliente.rpc('ordem_dos_nos', { p_versao_id: linha.versao_id }),
  ]);

  const nos = ouErro<NoDoDetalhe[]>(nosCrus as RespostaDoBanco<NoDoDetalhe[]>, 'ler os nós');
  const itens = ouErro<{ no_id: string; valor: Valor }[]>(
    itensCrus as RespostaDoBanco<{ no_id: string; valor: Valor }[]>,
    'ler os itens da resposta',
  );
  const ordem = ouErro<{ no_id: string; ordem: number }[]>(
    ordemCrua as RespostaDoBanco<{ no_id: string; ordem: number }[]>,
    'ler a ordem das perguntas',
  );

  const porId = new Map(nos.map((n) => [n.id, n]));
  const posicao = new Map(ordem.map((o) => [o.no_id, Number(o.ordem)]));

  const rotuloPorNo: Record<string, string> = {};
  for (const n of nos) rotuloPorNo[n.id] = n.rotulo;

  const ordenados: ItemDaResposta[] = itens
    .map((i) => ({
      no_id: i.no_id,
      rotulo: porId.get(i.no_id)?.rotulo ?? '',
      subtipo: porId.get(i.no_id)?.subtipo ?? null,
      valor: i.valor,
      // `valor_texto()` é do SQL da fila; no detalhe a tela formata o valor
      // cru, porque é ele que o campo de edição precisa receber de volta.
      texto: null,
    }))
    .sort((a, b) => (posicao.get(a.no_id) ?? Infinity) - (posicao.get(b.no_id) ?? Infinity));

  return {
    resposta: linha,
    versao_numero: linha.versao?.numero ?? 0,
    convite_nome: linha.convite?.nome ?? null,
    convite_email: linha.convite?.email ?? null,
    no_atual_rotulo: linha.atual?.rotulo ?? null,
    no_atual_e_fim: linha.atual?.tipo === 'fim',
    itens: ordenados,
    rotuloPorNo,
  };
}

export type EnvioDaResposta = {
  readonly id: string;
  readonly canal: string;
  readonly destinatario: string;
  readonly assunto: string;
  readonly estado: EstadoEnvio;
  readonly tentativas: number;
  readonly erro: string | null;
  readonly criado_em: string;
};

/** §9: o que foi enfileirado por esta resposta, e não saiu. */
export async function enviosDaResposta(respostaId: string): Promise<EnvioDaResposta[]> {
  const r = await clientePainel()
    .from('envio')
    .select('id, canal, destinatario, assunto, estado, tentativas, erro, criado_em')
    .eq('resposta_id', respostaId)
    .order('criado_em');
  return ouErro<EnvioDaResposta[]>(r as RespostaDoBanco<EnvioDaResposta[]>, 'ler a fila de envios');
}

/**
 * Corrige o valor de um item.
 *
 * Não escreve `editada` e não escreve auditoria: os dois saem do trigger de
 * `0004`. Deixar a marca a cargo do front é deixá-la opcional, e uma marca
 * opcional some no primeiro caminho que alguém esquecer de cobrir.
 */
export async function editarItem(respostaId: string, noId: string, valor: Valor): Promise<void> {
  const { error } = await clientePainel()
    .from('resposta_item')
    .upsert({ resposta_id: respostaId, no_id: noId, valor }, { onConflict: 'resposta_id,no_id' });
  if (error) throw new ErroDaApi(error.message, error.code);
}

/* ══ Auditoria ═══════════════════════════════════════════════════════════ */

export type TrilhaDoFormulario = {
  readonly linhas: readonly Auditoria[];
  /**
   * §Estado degradado é dito: quando alguma das voltas não pôde ser feita, a
   * tela escreve o motivo em vez de mostrar uma trilha incompleta como se
   * fosse completa.
   */
  readonly avisoDeAlcance: string | null;
  /**
   * `true` quando o teto cortou a trilha — o que ficou de fora é o mais
   * antigo. Sem esta marca a trilha some pelo rodapé sem dizer nada, que é a
   * mesma mentira do aviso de alcance, só que mais difícil de perceber.
   */
  readonly truncada: boolean;
  /** O teto aplicado, para a tela poder escrevê-lo em número. */
  readonly limite: number;
};

/** O teto de linhas da trilha. Quem o mudar muda o texto do aviso junto. */
export const TETO_DA_TRILHA = 200;

/**
 * A trilha de um formulário.
 *
 * `auditoria` guarda `entidade` e `entidade_id`, e não `formulario_id` — o
 * trigger é genérico de propósito, para tabela nova nascer auditada sem que
 * ninguém lembre. O preço é este: alcançar "o que aconteceu NESTE formulário"
 * custa várias voltas, uma por forma de a linha se ligar ao formulário.
 *
 * As formas são três, e nenhuma delas cobre as outras:
 *
 *  1. **`entidade_id` é o próprio alvo** — o formulário e as respostas dele.
 *     Os ids das respostas vêm de uma consulta à parte.
 *  2. **`formulario_id` mora dentro do corpo** — `formulario_versao`,
 *     `convite` e `resposta`.
 *  3. **`versao_id` mora dentro do corpo** — `no` e `aresta`. É por aqui que
 *     "alguém reescreveu a pergunta e apagou uma aresta na véspera" entra na
 *     trilha; sem esta volta, o proprietário abria a Auditoria e via nada,
 *     enquanto as linhas existiam em `auditoria`.
 *
 * As voltas 2 e 3 são feitas **duas vezes cada**, contra `depois` e contra
 * `antes`: `auditar()` grava DELETE só com `antes`, e apagar é exatamente o
 * que mais importa procurar numa trilha.
 *
 * `resposta_item` tem `entidade_id` do item e o vínculo só existe dentro do
 * `depois` — é a sétima volta.
 *
 * Cada volta que o banco recusar é NOMEADA no aviso, em vez de sumir: trilha
 * incompleta que se apresenta como completa é pior que trilha ausente.
 */
export async function trilhaDoFormulario(
  formularioId: string,
  limite = TETO_DA_TRILHA,
): Promise<TrilhaDoFormulario> {
  const cliente = clientePainel();

  const idsCrus = await cliente.from('resposta').select('id').eq('formulario_id', formularioId);
  const respostaIds = ouErro<{ id: string }[]>(
    idsCrus as RespostaDoBanco<{ id: string }[]>,
    'ler as respostas do formulário',
  ).map((r) => r.id);

  const versoesCruas = await cliente
    .from('formulario_versao')
    .select('id')
    .eq('formulario_id', formularioId);
  const versaoIds = ouErro<{ id: string }[]>(
    versoesCruas as RespostaDoBanco<{ id: string }[]>,
    'ler as versões do formulário',
  ).map((v) => v.id);

  const grupos: Auditoria[][] = [];
  const semAlcance: string[] = [];
  /** Volta que voltou cheia até o teto: há mais linha do que coube. */
  let algumaNoTeto = false;

  const base = () =>
    cliente.from('auditoria').select('*').order('em', { ascending: false }).limit(limite);

  async function volta(oQue: string, consulta: PromiseLike<unknown>): Promise<void> {
    const r = (await consulta) as RespostaDoBanco<Auditoria[]>;
    if (r.error) {
      semAlcance.push(`${oQue} (${r.error.message})`);
      return;
    }
    const linhas = r.data ?? [];
    if (linhas.length >= limite) algumaNoTeto = true;
    grupos.push(linhas);
  }

  // A lista de uuids como o PostgREST a quer: `in.(a,b,c)`. Uuid não tem
  // vírgula nem aspas, então não há o que escapar.
  const emLista = (ids: readonly string[]) => `(${ids.join(',')})`;

  const voltas: Promise<void>[] = [
    volta('o formulário e as respostas', base().in('entidade_id', [formularioId, ...respostaIds])),
    volta('as versões e os convites', base().filter('depois->>formulario_id', 'eq', formularioId)),
    volta(
      'as versões e os convites apagados',
      base().filter('antes->>formulario_id', 'eq', formularioId),
    ),
  ];

  if (versaoIds.length > 0) {
    voltas.push(
      volta(
        'as perguntas e as ligações do grafo',
        base()
          .in('entidade', ['no', 'aresta'])
          .filter('depois->>versao_id', 'in', emLista(versaoIds)),
      ),
      volta(
        'as perguntas e as ligações apagadas',
        base()
          .in('entidade', ['no', 'aresta'])
          .filter('antes->>versao_id', 'in', emLista(versaoIds)),
      ),
    );
  }

  if (respostaIds.length > 0) {
    voltas.push(
      volta(
        'as edições de valor de resposta',
        base()
          .eq('entidade', 'resposta_item')
          .filter('depois->>resposta_id', 'in', emLista(respostaIds)),
      ),
    );
  }

  await Promise.all(voltas);

  // As voltas se sobrepõem de propósito (uma resposta cai na 1 e na 2): a
  // mesma linha vindo duas vezes é mais barato que uma volta a menos.
  const porId = new Map<number, Auditoria>();
  for (const grupo of grupos) for (const linha of grupo) porId.set(linha.id, linha);

  // Intercalar por data não é agregação: é ordenação do que o banco já mandou.
  const combinado = [...porId.values()].sort((a, b) => (a.em < b.em ? 1 : a.em > b.em ? -1 : 0));

  const avisoDeAlcance =
    semAlcance.length === 0
      ? null
      : `Esta trilha está incompleta — o banco recusou o filtro de: ${semAlcance.join('; ')}. ` +
        'O resto do que aconteceu neste formulário está listado abaixo.';

  return {
    linhas: combinado.slice(0, limite),
    avisoDeAlcance,
    truncada: combinado.length > limite || algumaNoTeto,
    limite,
  };
}

/* ══ Pessoas ═════════════════════════════════════════════════════════════ */

export async function pessoasDaOrganizacao(organizacaoId: string): Promise<Perfil[]> {
  const r = await clientePainel()
    .from('perfil')
    .select('*')
    .eq('organizacao_id', organizacaoId)
    .order('nome');
  return ouErro<Perfil[]>(r as RespostaDoBanco<Perfil[]>, 'listar as pessoas');
}

/**
 * Trocar o papel de alguém. Pede a linha de volta, e recusa o silêncio.
 *
 * `perfil_gerir` exige ser proprietário no `using` E no `with check`. Sem o
 * `.select()`, o proprietário que se rebaixava perdia no mesmo instante o
 * direito de se restaurar, e a chamada de volta não fazia nada — sem erro, sem
 * aviso, com a tela mostrando sucesso. Ver `exigirEfeito` em `api.ts`.
 */
export async function trocarPapel(
  usuarioId: string,
  organizacaoId: string,
  papel: Papel,
): Promise<void> {
  const r = await clientePainel()
    .from('perfil')
    .update({ papel })
    .eq('usuario_id', usuarioId)
    .eq('organizacao_id', organizacaoId)
    .select('usuario_id');
  exigirEfeito(r, 'trocar o papel');
}

export async function definirAtivo(
  usuarioId: string,
  organizacaoId: string,
  ativo: boolean,
): Promise<void> {
  const r = await clientePainel()
    .from('perfil')
    .update({ ativo })
    .eq('usuario_id', usuarioId)
    .eq('organizacao_id', organizacaoId)
    .select('usuario_id');
  exigirEfeito(r, ativo ? 'reativar a pessoa' : 'desativar a pessoa');
}

/* ══ Exportação: o que o banco entrega ═══════════════════════════════════ */

export type ColunaDeExportacao = {
  readonly versao_id: string;
  readonly versao_numero: number;
  readonly no_id: string;
  readonly ordem: number;
  readonly rotulo: string;
};

export type LinhaExportada = {
  readonly resposta_id: string;
  readonly versao_numero: number;
  readonly estado: EstadoResposta;
  readonly etiquetas: readonly string[];
  readonly editada: boolean;
  readonly convite_nome: string | null;
  readonly convite_email: string | null;
  readonly iniciada_em: string;
  readonly concluida_em: string | null;
  readonly fim_rotulo: string | null;
  /** Chaveado pelo RÓTULO da coluna, como `colunas_de_exportacao` os nomeia. */
  readonly itens: Readonly<Record<string, string | null>>;
};

export async function colunasDeExportacao(formularioId: string): Promise<ColunaDeExportacao[]> {
  const r = await clientePainel().rpc('colunas_de_exportacao', {
    p_formulario_id: formularioId,
  });
  const cru = ouErro<Record<string, unknown>[]>(
    r as RespostaDoBanco<Record<string, unknown>[]>,
    'ler as colunas de exportação',
  );
  return cru.map((c) => ({
    versao_id: String(c['versao_id']),
    versao_numero: inteiro(c['versao_numero']),
    no_id: String(c['no_id']),
    ordem: inteiro(c['ordem']),
    rotulo: String(c['rotulo']),
  }));
}

/**
 * As linhas da planilha, com o MESMO filtro que a lista está mostrando.
 *
 * §10 manda a exportação respeitar o filtro visível, e o filtro é aplicado no
 * banco — pela mesma função e com os mesmos parâmetros de
 * `respostas_do_formulario`. Filtrar de novo aqui seria uma segunda
 * implementação da mesma regra, e é entre duas implementações da mesma regra
 * que a planilha começa a trazer linha que a lista não mostrava.
 */
export async function exportarRespostas(
  formularioId: string,
  filtro: FiltroDeRespostas = SEM_FILTRO,
): Promise<LinhaExportada[]> {
  const periodo = limitesDoPeriodo(filtro);
  const r = await clientePainel().rpc('exportar_respostas', {
    p_formulario_id: formularioId,
    p_estado: filtro.estado,
    p_etiqueta: filtro.etiqueta,
    p_de: periodo.de,
    p_ate: periodo.ate,
    p_busca: filtro.busca,
  });
  const cru = ouErro<Record<string, unknown>[]>(
    r as RespostaDoBanco<Record<string, unknown>[]>,
    'exportar as respostas',
  );
  return cru.map((l) => ({
    resposta_id: String(l['resposta_id']),
    versao_numero: inteiro(l['versao_numero']),
    estado: l['estado'] as EstadoResposta,
    etiquetas: (l['etiquetas'] as string[] | null) ?? [],
    editada: l['editada'] === true,
    convite_nome: (l['convite_nome'] as string | null) ?? null,
    convite_email: (l['convite_email'] as string | null) ?? null,
    iniciada_em: String(l['iniciada_em']),
    concluida_em: (l['concluida_em'] as string | null) ?? null,
    fim_rotulo: (l['fim_rotulo'] as string | null) ?? null,
    itens: (l['itens'] as Record<string, string | null> | null) ?? {},
  }));
}
